/**
 * Centralized status color configuration.
 *
 * The canonical values live as CSS variables (`--status-*`) in
 * src/assets/styles/tailwind/index.css. DOM consumers get `var(...)` /
 * `color-mix(...)` strings so themes stay in sync; canvas consumers
 * (Chart.js) can't parse CSS variables, so chart helpers resolve them
 * at call time with the hex fallbacks below.
 */

export interface StatusColor {
  background: string;
  text: string;
  border: string;
}

// Hex fallbacks — keep in sync with the --status-* variables in
// src/assets/styles/tailwind/index.css. Only used where a concrete
// color is required (canvas) or the variable is unavailable.
const STATUS_HEX: Record<string, string> = {
  draft: '#6c757d',
  submitted: '#17a2b8',
  under_review: '#17a2b8',
  approved: '#007bff',
  in_progress: '#0056b3',
  pending: '#ffc107',
  paid: '#20c997',
  completed: '#28a745',
  cancelled: '#dc3545',
  on_hold: '#fd7e14',
};

// under_review shares the submitted (info) color.
const statusVar = (status: string): string =>
  `var(--status-${(status === 'under_review' ? 'submitted' : status).replace(/_/g, '-')}, ${STATUS_HEX[status]})`;

// Statuses whose solid background needs dark text for contrast.
const DARK_TEXT_STATUSES = new Set(['pending']);

const solid = (status: string): StatusColor => ({
  background: statusVar(status),
  text: DARK_TEXT_STATUSES.has(status) ? '#212529' : '#ffffff',
  border: statusVar(status),
});

const light = (status: string): StatusColor => ({
  background: `color-mix(in srgb, ${statusVar(status)} 10%, transparent)`,
  text: statusVar(status),
  border: `color-mix(in srgb, ${statusVar(status)} 30%, transparent)`,
});

const STATUS_KEYS = Object.keys(STATUS_HEX);

export const STATUS_COLORS: Record<string, StatusColor> = Object.fromEntries(
  STATUS_KEYS.map((s) => [s, solid(s)])
);

// Light versions for subtle backgrounds
export const STATUS_COLORS_LIGHT: Record<string, StatusColor> = Object.fromEntries(
  STATUS_KEYS.map((s) => [s, light(s)])
);

// Chart colors — concrete hex values (Chart.js canvas can't resolve CSS
// variables). Prefer getChartColor(), which reads the live variable.
// Module-specific statuses beyond the transaction palette. Canvas (Chart.js)
// can't consume CSS variables, so this file remains the single sanctioned
// home for chart hexes (kept visually consistent with the token families).
const MODULE_STATUS_HEX: Record<string, string> = {
  // proposals
  pending_review: '#17a2b8',
  sent: '#0693e3',
  accepted: '#28a745',
  rejected: '#dc3545',
  expired: '#6c757d',
  archived: '#a3a3a3',
  // payments
  initiated: '#0693e3',
  captured: '#20c997',
  failed: '#dc3545',
  refunded: '#fd7e14',
  // documents
  sent_to_client: '#0693e3',
  signed: '#28a745',
  // projects
  active: '#0056b3',
};

export const CHART_COLORS: Record<string, string> = { ...STATUS_HEX, ...MODULE_STATUS_HEX };

// Priority colors
const PRIORITY_HEX: Record<string, { bg: string; text: string }> = {
  low: { bg: '#28a745', text: '#ffffff' },
  normal: { bg: '#17a2b8', text: '#ffffff' },
  high: { bg: '#ffc107', text: '#212529' },
  urgent: { bg: '#dc3545', text: '#ffffff' },
};

export const PRIORITY_COLORS: Record<string, StatusColor> = Object.fromEntries(
  Object.entries(PRIORITY_HEX).map(([p, { bg, text }]) => [
    p,
    { background: bg, text, border: bg },
  ])
);

/**
 * Get status color configuration
 * @param status - The transaction status
 * @param variant - 'solid' for badges, 'light' for backgrounds
 */
export function getStatusColor(status: string, variant: 'solid' | 'light' = 'solid'): StatusColor {
  const normalizedStatus = status.toLowerCase().replace(/\s+/g, '_');
  const colors = variant === 'solid' ? STATUS_COLORS : STATUS_COLORS_LIGHT;

  return colors[normalizedStatus] || {
    background: statusVar('draft'),
    text: '#ffffff',
    border: statusVar('draft'),
  };
}

/**
 * Get chart color for a status — resolves the live CSS variable so charts
 * follow the theme, falling back to the static hex.
 * @param status - The transaction status
 */
export function getChartColor(status: string): string {
  const normalizedStatus = status.toLowerCase().replace(/\s+/g, '_');
  const fallback = CHART_COLORS[normalizedStatus] || CHART_COLORS.draft;
  if (typeof window === 'undefined' || !(normalizedStatus in STATUS_HEX)) return fallback;
  const varName = `--status-${(normalizedStatus === 'under_review' ? 'submitted' : normalizedStatus).replace(/_/g, '-')}`;
  const resolved = getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
  return resolved || fallback;
}

/**
 * Get priority color configuration
 * @param priority - The transaction priority
 */
export function getPriorityColor(priority: string): StatusColor {
  const normalizedPriority = priority.toLowerCase();
  return PRIORITY_COLORS[normalizedPriority] || PRIORITY_COLORS.normal;
}
